import React, { useMemo } from 'react';
import { JsonForms } from '@jsonforms/react';
import { materialRenderers, materialCells } from '@jsonforms/material-renderers';
import {
  createAjv,
  type JsonSchema,
  type UISchemaElement,
  type JsonFormsCore,
  type ValidationMode,
} from '@jsonforms/core';
import { Box, Alert } from '@mui/material';
import { entitySelectRendererEntry } from '@/components/jsonForms/renderers/EntitySelectRenderer';
import { sizeTypeaheadRendererEntry } from '@/components/jsonForms/renderers/SizeTypeaheadRenderer';
import { textAreaRendererEntry } from '@/components/jsonForms/renderers/TextAreaRenderer';
import { groupLayoutRendererEntry } from '@/components/jsonForms/renderers/GroupLayoutRenderer';

interface JsonFormsRendererProps {
  schema: JsonSchema;
  uiSchema?: UISchemaElement;
  data: Record<string, unknown>;
  onChange?: (data: Record<string, unknown>) => void;
  onValidationChange?: (isValid: boolean, errors: unknown[]) => void;
  readonly?: boolean; // Standard readonly support
  validationMode?: ValidationMode;
  /** Optional map of scope (e.g. "#/properties/primary_location_id") → label override */
  labelOverrides?: Record<string, string>;
}

// createAjv() from @jsonforms/core already includes format validation
const ajv = createAjv();

export const JsonFormsRenderer: React.FC<JsonFormsRendererProps> = ({
  schema,
  uiSchema,
  data,
  onChange,
  onValidationChange,
  readonly = false, // Default to editable
  validationMode = 'ValidateAndShow',
  labelOverrides,
}) => {  /**
   * Renderer Registry with Custom Renderers
   *
   * Order matters: Later entries can override earlier ones if they have higher rank.
   * See individual renderer files for ranking strategy documentation.
   *
   * Registered renderers:
   * - materialRenderers (rank 1-3): Default Material-UI renderers from @jsonforms/material-renderers
   * - textAreaRendererEntry (rank 4): Multiline text areas with placeholder support
   * - groupLayoutRendererEntry (rank 5): Card-based group layouts with reduced padding
   * - entitySelectRendererEntry (rank 5): Autocomplete for entity selection
   * - sizeTypeaheadRendererEntry (rank 5): freeSolo autocomplete for catalog size field
   */  const renderers = useMemo(
    () => [
      ...materialRenderers,
      entitySelectRendererEntry,
      sizeTypeaheadRendererEntry,
      textAreaRendererEntry,
      groupLayoutRendererEntry,
    ],
    [],
  );
  const handleChange = (state: Pick<JsonFormsCore, 'data' | 'errors'>) => {
    if (!readonly && onChange) {
      onChange(state.data as Record<string, unknown>);
    }
    if (onValidationChange) {
      const errs = state.errors ?? [];
      onValidationChange(errs.length === 0, errs as unknown[]);
    }
  };

  // Fallback UI schema if none provided
  const defaultUISchema: UISchemaElement = {
    type: 'VerticalLayout',
    elements: Object.keys((schema as JsonSchema).properties || {}).map((key) => ({
      type: 'Control',
      scope: `#/properties/${key}`,
    })),
  };

  // Apply label overrides to the UI schema when provided
  const resolvedUISchema = useMemo(() => {
    const base = uiSchema || defaultUISchema;
    if (!labelOverrides || Object.keys(labelOverrides).length === 0) return base;

    const applyOverrides = (element: unknown): unknown => {
      if (!element || typeof element !== 'object') return element;
      const el = element as Record<string, unknown>;
      let result = { ...el };

      // If this element has a scope that matches an override, replace the label
      if (typeof result.scope === 'string' && labelOverrides[result.scope]) {
        result = { ...result, label: labelOverrides[result.scope] };
      }

      // Recurse into elements array
      if (Array.isArray(result.elements)) {
        result = { ...result, elements: (result.elements as unknown[]).map(applyOverrides) };
      }
      return result;
    };

    return applyOverrides(base) as UISchemaElement;
  }, [uiSchema, labelOverrides]);

  if (!schema) {
    return <Alert severity="error">No schema provided for form rendering</Alert>;
  }

  return (
    <Box
      sx={{
        width: '100%',
        '& .MuiFormControl-root': {
          marginBottom: 2,
          minWidth: 200,
        },
        '& .MuiTextField-root': {
          marginBottom: 2,
        },
        '& .MuiFormGroup-root': {
          marginBottom: 3,
        },
        '& .MuiAccordion-root': {
          marginBottom: 2,
        },
        // JSON Forms Categorization spacing fix - target the div after MuiAppBar-root
        '& .MuiAppBar-root + div': {
          marginTop: '24px !important',
        },
      }}
    >
      <JsonForms
        schema={schema}
        uischema={resolvedUISchema}
        data={data}
        renderers={renderers}
        cells={materialCells}
        onChange={handleChange}
        ajv={ajv}
        readonly={readonly} // ✅ Standard JSON Forms readonly prop
        validationMode={validationMode}
      />
    </Box>
  );
};
